import { getCurrentUserId } from './database';
import proxyapi from './ProxyApi';

export const createShareLink = async (itemType, itemId, options = {}) => {
  try {
    const userId = await getCurrentUserId();
    
    if (!userId) {
      throw new Error('User not authenticated');
    }
    
    if (!itemType || !itemId) {
      throw new Error('Missing required parameters: itemType and itemId are required');
    }
    
    // Validate item type
    if (itemType !== 'view' && itemType !== 'flow') {
      throw new Error('Invalid item type: must be "view" or "flow"');
    }
    
    const response = await proxyapi.post('/api/v1/shares', {
      userId,
      itemType,
      itemId,
      versionId: options.versionId, // Add versionId to API request
      expirationDays: options.expirationDays,
      requirePassword: !!options.password,
      password: options.password
    });
    
    if (response.data.status === 'error') {
      throw new Error(response.data.message || 'Failed to create share link');
    }
    
    return response.data;
  } catch (error) {
    console.error('Error creating share link:', error);
    // Return more detailed error information
    const errorMessage = error.response?.data?.message || error.message || 'Unknown error occurred';
    throw new Error(errorMessage);
  }
};

// Updated function in services/sharing.js
export const getUserShares = async () => {
  try {
    const userId = await getCurrentUserId();
    if (!userId) {
      console.error('User not authenticated');
      return [];
    }
    
    const response = await proxyapi.get(`/api/v1/shares/user/${userId}?include_details=true`);
    
    if (response.data.status === 'success') {
      return response.data.shares;
    }
    return [];
  } catch (error) {
    console.error('Error fetching user shares:', error);
    return [];
  }
};

export const deleteShareLink = async (shareId) => {
  try {
    const userId = await getCurrentUserId();
    
    if (!userId) {
      throw new Error('User not authenticated');
    }
    
    if (!shareId) {
      throw new Error('Share ID is required');
    }
    
    const response = await proxyapi.delete(`/api/v1/shares/${shareId}`, {
      data: { userId }
    });
    
    if (response.data.status === 'error') {
      throw new Error(response.data.message || 'Failed to delete share link');
    }
    
    return true;
  } catch (error) {
    console.error('Error deleting share:', error);
    throw error;
  }
};

export const getSharedItem = async (shareId, password = null) => {
  try {
    if (!shareId) {
      throw new Error('Share ID is required');
    }
    
    // Build URL with or without password
    let url = `/api/v1/shares/${shareId}`;
    if (password) {
      url += `?password=${encodeURIComponent(password)}`;
    }
      
    const response = await proxyapi.get(url);
    
    // Explicitly handle different statuses from the API
    if (response.data.status === 'incorrect_password') {
      return { status: 'incorrect_password' };
    }
    
    if (response.data.status === 'password_required') {
      return { status: 'password_required' };
    }
    
    if (response.data.status === 'error') {
      // Handle view limit exceeded specifically
      if (response.data.code === 'view_limit_exceeded') {
        return { 
          status: 'error',
          code: 'view_limit_exceeded',
          message: 'View limit exceeded'
        };
      }
      throw new Error(response.data.message || 'Error accessing shared item');
    }
    
    return response.data;
  } catch (error) {
    console.error('Error accessing shared item:', error);
    // Check if this is a view limit exceeded error from the server
    if (error.response?.data?.code === 'view_limit_exceeded') {
      return {
        status: 'error',
        code: 'view_limit_exceeded',
        message: 'View limit exceeded'
      };
    }
    // Provide more context in the error
    const message = error.response?.data?.message || error.message || 'Could not access shared content';
    throw new Error(message);
  }
};